/**
 * Macro #20 §18/§19 — canonical owner moderation state for a single target.
 *
 * ONE source of truth (`GET /api/v1/moderation/target/{type}/{id}`) reused by
 * EVERY owner surface (Dashboard, Parcel/Territory, Artwork, Canvas). No surface
 * re-implements moderation logic. Non-owners / not-found / not-moderated all
 * resolve to "not moderated" and the banner renders nothing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { moderationApi, ModerationApiError, type OwnerTargetView, type TargetType } from "../../lib/moderation-api";

export type ModerationTargetState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "clear" } // owner, not moderated (or not the owner / not found)
  | { status: "moderated"; view: OwnerTargetView };

export function useModerationTarget(
  targetType: TargetType | null,
  targetId: string | null,
  enabled = true,
): { state: ModerationTargetState; refetch: () => void } {
  const [state, setState] = useState<ModerationTargetState>({ status: "idle" });
  const reqRef = useRef(0);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || !targetType || !targetId) {
      setState({ status: "idle" });
      return;
    }
    const myId = ++reqRef.current;
    const controller = new AbortController();
    setState({ status: "loading" });
    moderationApi
      .targetView(targetType, targetId, controller.signal)
      .then((view) => {
        if (myId !== reqRef.current) return;
        // Show the banner for an ACTIVE case (moderated) OR a closed case that
        // still carries an unread admin message (Macro #20 b) so the owner can
        // read and clear it. Otherwise nothing to show.
        const show = view.moderated || (view.case != null && !!view.owner_unread);
        setState(show ? { status: "moderated", view } : { status: "clear" });
      })
      .catch((e) => {
        if (controller.signal.aborted || myId !== reqRef.current) return;
        // 403 (not owner) / 404 (not found) / anything else → nothing to show.
        void (e as ModerationApiError);
        setState({ status: "clear" });
      });
    return () => controller.abort();
  }, [targetType, targetId, enabled, tick]);

  return { state, refetch };
}
